import { AnchorHTMLAttributes, type JSX } from "react"
import Link from "next/link"

import { MultilinkStoryblok } from "@/components/storyblok/component-types"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"

// see: https://www.storyblok.com/docs/guide/in-depth/rendering-the-link-field

interface StoryblokMultilinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "target" | "rel"> {
  link?: MultilinkStoryblok
  sbLocale?: string
  label?: string
}

const StoryblokMultilink = ({
  link,
  children,
  sbLocale,
  label,
  ...restProps
}: StoryblokMultilinkProps): JSX.Element => {

  if (link && link?.linktype === "story" && link.cached_url) {
    const href = resolveStoryblokStoryHref(link.cached_url, sbLocale, link.story)
    return (
      <Link prefetch={false} {...restProps} href={href} target={link.target} aria-label={label}>
        {children}
      </Link>
    )
  }

  if (link?.linktype === "url" && link.url) {
    return (
      <a
        aria-label={label}
        href={link.url}
        {...restProps}
        target={link.target}
        rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    )
  }

  /**
   * @todo The link to the asset should not be executed. Instead, the asset is displayed in a lightbox as an overlay.
   */
  if (link?.linktype === "asset" && link.url) {
    return (
      <a aria-label={label} href={link.url} {...restProps} target={link.target}>
        {children}
      </a>
    )
  }

  if (link?.linktype === "email" && link.email) {
    return (
      <a aria-label={label} href={`mailto:${link.email}`} {...restProps} target={link.target}>
        {children}
      </a>
    )
  }

  // If link type is unknown or missing, return children without a link
  return (
    <span aria-label={label} {...restProps}>
      {children}
    </span>
  )
}

export default StoryblokMultilink
